import React, {useRef} from 'react';

import {ProcessesFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import BenchlingConfig from 'BenchlingConfig';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {GetAnalyticsDashboard} from 'insights/analytics-dashboards/details/graphql/__generated/GetAnalyticsDashboard';
import UniversalBlockGlobalClientStates from 'insights/universal-blocks/UniversalBlockGlobalClientStates';
import {RunQueryState} from 'insights/warehouse/AnalyticsWarehouseTypes';
import {convertRunQueryStateToLegacyState} from 'insights/warehouse/convertRunQueryOutputToLegacyOutput';
import {useWritableContext} from 'insights/WritableContext';
import useSelectorsById, {selectorsAreLoaded} from 'notebook/structured-tables/hooks/useSelectorsById';
import {useProcessTaskSchemasForCreateWithLoading} from 'processes/CreateProcessUtil';
import Menu from 'ui-library/menus/Menu';
import Section from 'ui-library/menus/Section';

import BaseBlockOverflowDropdown from './BaseBlockOverflowDropdown';
import {SHARED_MENU_PROPS} from './BlockOverflowMenuConstants';
import getAddToWorklistOption from './getAddToWorklistOption';
import getCloneBlockOption from './getCloneBlockOption';
import getCopyAsTSVOption from './getCopyAsTSVOption';
import getCreateProcessTasksOption from './getCreateProcessTasksOption';
import getDeleteBlockOption from './getDeleteBlockOption';
import getDownloadAsCSVOption, {ExportBlockFn} from './getDownloadAsCSVOption';
import getExecuteProcessTasksOption from './getExecuteProcessTasksOption';
import getExecuteRequestOption from './getExecuteRequestOption';

type AnalyticsDashboard = ExtractByTypename<
  GetAnalyticsDashboard['analyticsDashboard'],
  'AnalyticsDashboardNode'
>;

interface Actions {
  cloneBlock: () => void;
  deleteBlock: () => void;
  exportBlock: ExportBlockFn;
}

/** @package */
export interface AnalyticsBlockOverflowDropdownProps {
  actions: Actions;
  blockViewStateId: string;
  className?: string;
  dashboardAttrs: Pick<AnalyticsDashboard, 'analyticsQueries'>;
  runQueryState: RunQueryState;
}

/**
 * Overflow menu for dashboard editor's block
 *
 * Options to download CSV, copy TSV, add to worklist, duplicate block, and delete block
 */
export default function AnalyticsBlockOverflowDropdown({
  actions,
  blockViewStateId,
  className,
  dashboardAttrs,
  runQueryState,
}: AnalyticsBlockOverflowDropdownProps): JSX.Element {
  const processTaskSchemas = useProcessTaskSchemasForCreateWithLoading();
  const selectorsById = useSelectorsById({includeArchivedSelectors: true});
  const [sortState] = UniversalBlockGlobalClientStates.sortState.useGlobalClientState(blockViewStateId);
  const writable = useWritableContext();
  const tsvTextRef = useRef<string | null>('');
  const optionProps = {
    actions,
    runQueryState: convertRunQueryStateToLegacyState(runQueryState),
    showRawOutput: false,
    sortState,
    tsvTextRef,
  };
  return (
    <BaseBlockOverflowDropdown
      className={className}
      menu={
        <Menu {...SHARED_MENU_PROPS}>
          <Section>
            {getDownloadAsCSVOption(optionProps)}
            {getCopyAsTSVOption(optionProps)}
            {BenchlingConfig.WORKLISTS_ENABLE_WORKLISTS ? getAddToWorklistOption(optionProps) : null}
            {BenchlingConfig.ENABLE_REQUESTS ? getExecuteRequestOption(optionProps) : null}
            {isFeatureEnabled({feature: ProcessesFeature.PROCESSES_EDIT}) &&
            BenchlingConfig.WORKFLOWS_ENABLE_NEW_TASK_CREATION_SHORTCUT &&
            selectorsAreLoaded(selectorsById)
              ? getCreateProcessTasksOption({runQueryState, processTaskSchemas, selectorsById})
              : null}
            {isFeatureEnabled({feature: ProcessesFeature.PROCESSES_EDIT})
              ? getExecuteProcessTasksOption(optionProps)
              : null}
          </Section>
          {writable && (
            <Section>
              {getCloneBlockOption({...optionProps, dashboardAttrs})}
              {getDeleteBlockOption(optionProps)}
            </Section>
          )}
        </Menu>
      }
    />
  );
}
